<template>
  <div v-if="editorStore.openedPanel === 'fx'">
    <FxDrawer
      :foreground-type="editorStore.selectedImage.meta.foregroundType"
      :selected-image-state="currentPersistentStore"
      :getDataURL="props.getDataURL"
      :stage="props.stage"
      :stageHd="props.stageHd"
      :isCanvaIntegrationEnabled="canvaEditorMvpEnabled"
      :refreshAllHdLayers="props.refreshAllHdLayers"
      :persistentStore="props.currentPersistentStore"
      @close-panel="editorStore.closePanel"
    />
  </div>
  <div v-if="editorStore.openedPanel === 'addBackground'">
    <DrawerAddBackground
      @close-panel="() => props.togglePanel('addBackground')"
      @reset="props.restoreOriginalResult"
      :foreground-type="editorStore.selectedImage.meta.foregroundType"
      :selected-image-state="props.currentPersistentStore"
    />
  </div>
  <div v-if="editorStore.openedPanel === 'eraseRestore'">
    <DrawerEraseRestore
      :editor="props.editor"
      :stage="props.stage"
      :stageHd="props.stageHd"
      :scale="props.scale"
      :getDataURL="props.getDataURL"
      :isCanvaIntegrationEnabled="canvaEditorMvpEnabled"
      :refreshAllHdLayers="props.refreshAllHdLayers"
      :persistentStore="props.currentPersistentStore"
      @close-panel="() => props.togglePanel('eraseRestore')"
      @reset="restoreOriginalResult"
    />
  </div>
  <div v-if="editorStore.openedPanel === 'adjust'">
    <DrawerAdjust
      :getDataURL="props.getDataURL"
      :editor="props.editor"
      :stage="props.stage"
      :stageHd="props.stageHd"
      :persistentStore="props.currentPersistentStore"
      :refreshAllHdLayers="props.refreshAllHdLayers"
      @close-panel="() => props.togglePanel('adjust')"
    />
  </div>
  <div v-if="editorStore.openedPanel === 'design'">
    <DrawerDesign
      :getDataURL="props.getDataURL"
      :editor="props.editor"
      :stage="props.stage"
      :stageHd="props.stageHd"
      :persistentStore="props.currentPersistentStore"
      :refreshAllHdLayers="props.refreshAllHdLayers"
      @close-panel="() => props.togglePanel('design')"
    />
  </div>
</template>
<script setup lang="ts">
import { Panel, useEditorStore } from "@/stores/editor_store";
import DrawerAddBackground from "@/components/upload/drawer_add_background.vue";
import DrawerEraseRestore from "@/components/upload/drawer_erase_restore.vue";
import DrawerAdjust from "@/components/upload/drawer_adjust.vue";
import DrawerDesign from "@/components/upload/drawer_design.vue";
import FxDrawer from "@/components/upload/fx_drawer.vue";
import { Stage } from "konva/lib/Stage";
import { useMonitorSize } from "@/composables/monitor_size";
import { computed, onMounted, onUnmounted, nextTick } from "vue";
import Flipper from "@/modules/flipper";
import { PersistentStore } from "@/stores/persistent_store";
import { Image } from "@/modules/internal_api/image";

interface DrawerManagerProps {
  currentPersistentStore: PersistentStore;
  editor: HTMLElement;
  stage: Stage;
  stageHd: Stage;
  scale: number;
  restoreOriginalResult: () => void;
  togglePanel: (id: Panel) => void;
  getDataURL: Function;
  refreshAllHdLayers: (image: Image) => Promise<void>;
}

const props = defineProps<DrawerManagerProps>();
const editorStore = useEditorStore();
const { isMobileLarge } = useMonitorSize();

const canvaEditorMvpEnabled = computed(() => {
  return Flipper.isEnabled("canva_in_the_editor");
});

const closeDesktopOnlyPanels = () => {
  if (isMobileLarge.value && canvaEditorMvpEnabled.value) {
    if (editorStore.openedPanel === "adjust") {
      props.togglePanel("adjust");
    }
    if (editorStore.openedPanel === "design") {
      props.togglePanel("design");
    }
  }
};

const handleResize = () => {
  nextTick(() => closeDesktopOnlyPanels());
};

onMounted(() => {
  window.addEventListener("resize", handleResize);
});

onUnmounted(() => {
  window.removeEventListener("resize", handleResize);
});
</script>
